import { useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';

import type * as api from '@gitbook/api-client';
import { type ButtonProps, toast } from '@gitbook/spine-react';

import { apiClient, apiQuery } from '../api';
import { useRedirectToStripe } from './useRedirectToStripe';

type SiteCheckoutState = {
    buttonProps: Pick<ButtonProps, 'label' | 'leadingIcon' | 'trailingIcon' | 'kind'>;
    onPress: () => Promise<boolean>;
    state: api.OperationSpec<'previewSitePublishCheckout'>['ResponseBody'];
};

/**
 * Returns the checkout state of a site.
 */
export function useCheckoutSite(organization: string, site: api.Site): SiteCheckoutState {
    const redirectToStripe = useRedirectToStripe();
    const { data: checkout } = useSuspenseQuery(
        apiQuery('previewSitePublishCheckout', { organizationId: organization, plan: site.type })
    );

    return React.useMemo(() => {
        // No checkout required, let the user publish immediately.
        if (checkout.type === 'go-ahead') {
            return {
                state: checkout,
                buttonProps: {
                    label: 'Publish & pay',
                    kind: 'upgrade',
                },
                onPress: async () => {
                    return (
                        (await toast.catch(async () => {
                            await apiClient.sites.publishSite(organization, site.id);
                            return true;
                        }, 'Error while publishing site')) ?? false
                    );
                },
            };
        }
        if (checkout.type === 'trial-available' || checkout.type === 'extended-trial-available') {
            return {
                state: checkout,
                buttonProps: {
                    label: 'Publish & start trial',
                    kind: 'upgrade',
                },
                onPress: async () => {
                    return (
                        (await toast.catch(async () => {
                            await apiClient.sites.publishSite(organization, site.id);
                            return true;
                        }, 'Error while publishing site')) ?? false
                    );
                },
            };
        }

        // At this point, an upgrade is required.
        if (checkout.type === 'checkout') {
            return {
                state: checkout,
                buttonProps: {
                    label: 'Publish & pay',
                    kind: 'upgrade',
                },
                onPress: async () => {
                    return (
                        (await toast.catch(async () => {
                            const { data: results } = await apiClient.sites.publishSite(
                                organization,
                                site.id
                            );

                            if (results.type === 'checkout') {
                                await redirectToStripe(results.sessionId);
                                return true;
                            }
                            return true;
                        }, 'Error while publishing site')) ?? false
                    );
                },
            };
        }

        throw new Error('unknown checkout type');
    }, [checkout, organization, site.id, redirectToStripe]);
}
